import React from "react";
import styled from "styled-components";
import { ColumnsType, ColumnType } from "antd/es/table";
import {
  BasicBotData,
  BotType,
  LanguageNameSpaces,
  SortingOrder,
  WorkspaceType,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { MyBotsSort } from "@shared/features/bots/types";
import {
  getBotAmountDisplay,
  isGridPnlBotType,
} from "@shared/features/bots/utils";
import { OrderTypes } from "@shared/features/trades/common/types";
import { TV_ALERT_SYMBOL } from "@shared/features/bots/constants.ts";
import { AccountObjectType } from "@shared/features/settings/types";
import { formatDateRelative } from "@shared/utils/date";
import { SORT_ORDER_MAPPING } from "common/constants";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import BotChange from "features/bots/common/BotChange";
import BotMainIcon from "features/bots/MyBots/common/BotMainIcon";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import InactiveGroupIcon from "features/configuration/common/InactiveGroupIcon.tsx";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup.tsx";
import NewLookBotStatus from "features/bots/NewLook/NewLookBotStatus";
import { NewLookNextOrderCell as NextOrderCell } from "features/bots/NewLook/NewLookNextOrder";
import { getNewLookSettingsChips } from "features/bots/NewLook/newLookBotUtils";

interface NewLookBotsTableColumnsArgs {
  botType: BotType;
  sortBy?: MyBotsSort;
  sortDirection: SortingOrder;
  isChannelConfiguration?: boolean;
  workspace?: WorkspaceType;
}

/**
 * New look: the bots table as rows you can compare — the bot (icon, name,
 * what it trades · where) and its status, then figures right-aligned, each a
 * figure over a quiet meta line (PnL over the trades it closed, a level's
 * distance over its price), like the dashboard's rows.
 */
const useNewLookBotsTableColumns = ({
  botType,
  sortBy,
  sortDirection,
  isChannelConfiguration,
  workspace,
}: NewLookBotsTableColumnsArgs): ColumnsType<BasicBotData> => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isTradingViewBot = botType === BotType.TRADING_VIEW;
  const isHedgingBot = botType === BotType.HEDGING;
  const useGridPnl = isGridPnlBotType(botType);
  const usesGroup = isSignalsBot || isTradingViewSignalsBot;

  const sortable = (sort: MyBotsSort) => ({
    sorter: true,
    showSorterTooltip: false,
    sortOrder: sortBy === sort ? SORT_ORDER_MAPPING[sortDirection] : undefined,
  });
  const empty = <Muted>—</Muted>;

  const botColumn: ColumnType<BasicBotData> = {
    title: t("labels.name"),
    dataIndex: "name",
    key: "name",
    ...sortable(MyBotsSort.NAME),
    render: (_, botData) => {
      let symbol = botData.symbols?.[0];
      if (!symbol && (isTradingViewBot || isTradingViewSignalsBot)) {
        symbol = TV_ALERT_SYMBOL;
      }
      const what = usesGroup ? (
        <InactiveGroupIcon
          isInactive={isSignalsBot && botData.sub_group_is_active === false}
          style={GROUP_STYLE}
        >
          <Ellipsis>{botData.sub_group_name}</Ellipsis>
        </InactiveGroupIcon>
      ) : (
        <Ellipsis>{symbol}</Ellipsis>
      );
      return (
        <Bot>
          <BotMainIcon
            botData={botData}
            size={32}
            iconStyle={ICON_STYLE}
            showOneClickDefaultIcon={true}
            isGroupIcon={isTradingViewSignalsBot}
          />
          <Names>
            <Name title={botData.name}>{botData.name}</Name>
            <Sub>
              {what}
              {!isChannelConfiguration &&
                !isTradingViewSignalsBot &&
                botData.client_name && (
                  <>
                    <Dot />
                    <InactiveAccountIcon
                      hideIcon={true}
                      accountId={botData.client_id}
                      objectType={AccountObjectType.ACCOUNT}
                    >
                      <Ellipsis title={botData.client_name}>
                        {botData.client_name}
                      </Ellipsis>
                    </InactiveAccountIcon>
                  </>
                )}
            </Sub>
          </Names>
        </Bot>
      );
    },
  };

  const statusColumn: ColumnType<BasicBotData> = {
    title: t("labels.status"),
    dataIndex: "status",
    key: "status",
    render: (_, botData) => <NewLookBotStatus bot={botData} />,
  };

  const columns: ColumnsType<BasicBotData> = [botColumn, statusColumn];

  if (isTradingViewSignalsBot) {
    columns.push({
      title: t("labels.exchanges"),
      dataIndex: "exchanges",
      key: "exchanges",
      render: (_, botData) => (
        <ExchangesIconsGroup
          exchanges={botData.exchanges}
          size={24}
          maxCount={7}
        />
      ),
    });
    if (workspace !== WorkspaceType.ADMIN) {
      columns.push({
        title: t("labels.group"),
        dataIndex: "group",
        key: "group",
        ...sortable(MyBotsSort.GROUP),
        render: (_, botData) => <Text>{botData.sub_group_name || empty}</Text>,
      });
    }
  }

  if (isChannelConfiguration || isTradingViewSignalsBot) return columns;

  columns.push({
    title: t("labels.amount"),
    dataIndex: "amount",
    key: "amount",
    align: "right",
    ...sortable(MyBotsSort.AMOUNT),
    render: (_, botData) => (
      <End>
        <Figure>{getBotAmountDisplay(botData)}</Figure>
        <Meta>
          {getNewLookSettingsChips(botData, t("labels.long_short"))
            .map((chip) => chip.text)
            .join(" · ")}
        </Meta>
      </End>
    ),
  });

  const count = (botData: BasicBotData) => {
    if (botType === BotType.HEDGING) {
      return `${botData.number_of_rebalances || 0} ${t("labels.rebalances")}`;
    }
    if (useGridPnl) {
      return `${botData.take_profits_filled || 0} ${t(
        "labels.take_profits_filled"
      )}`;
    }
    return `${botData.number_of_closed_trades || 0} ${t(
      "labels.closed_trades"
    )}`;
  };
  columns.push({
    title: useGridPnl ? t("labels.realized_pnl") : t("labels.profit"),
    dataIndex: useGridPnl ? "realized_pnl" : "change",
    key: "pnl",
    align: "right",
    ...sortable(useGridPnl ? MyBotsSort.REALIZED_PNL : MyBotsSort.TOTAL_PROFIT),
    render: (_, botData) => (
      <End>
        <Figure>
          <BotChange botData={botData} isRealizedPnl={useGridPnl} />
        </Figure>
        <Meta>{count(botData)}</Meta>
      </End>
    ),
  });

  if (isHedgingBot) return columns;

  if (isSignalsBot || isTradingViewBot) {
    columns.push(
      {
        title: t("labels.open_trades"),
        dataIndex: "number_of_open_trades",
        key: "open_trades",
        align: "right",
        ...sortable(MyBotsSort.OPEN_TRADES),
        render: (_, botData) => (
          <End>
            <Figure>{botData.number_of_open_trades || 0}</Figure>
            {!!botData.number_of_open_trades && (
              <Meta>
                <BotChange botData={botData} isOpenTrades={true} />
              </Meta>
            )}
          </End>
        ),
      },
      {
        title: t("labels.last_trade_opened"),
        dataIndex: "last_trade",
        key: "last_trade",
        align: "right",
        ...sortable(MyBotsSort.LAST_TRADE_OPENED),
        render: (_, botData) =>
          botData.last_trade ? (
            <Text>
              {formatDateRelative(botData.last_trade, true, true, true)}
            </Text>
          ) : (
            empty
          ),
      }
    );
    return columns;
  }

  columns.push(
    {
      title: t("labels.last_entry"),
      dataIndex: "last_entry",
      key: "last_entry",
      align: "right",
      ...sortable(MyBotsSort.LAST_ENTRY),
      render: (_, botData) => {
        const lastEntry = botData.active_trade?.last_entry;
        if (!lastEntry) return empty;
        return (
          <End>
            <Figure>
              {lastEntry.target_number}/{lastEntry.number_of_targets}
            </Figure>
            <Meta>
              {lastEntry.time_passed} {t("common.ago")}
            </Meta>
          </End>
        );
      },
    },
    {
      title: t("labels.next_entry_or_stop"),
      dataIndex: "next_entry_or_stop",
      key: "next_entry_or_stop",
      align: "right",
      ...sortable(MyBotsSort.NEXT_ENTRY_OR_STOP),
      render: (_, botData) => {
        const nextEntry = botData.active_trade?.next_entry;
        const nextOrder = nextEntry || botData.active_trade?.next_stop;
        if (!nextOrder) return empty;
        return (
          <NextOrderCell
            symbol={botData.symbols?.[0]}
            exchange={botData.exchange}
            nextOrder={nextOrder}
            orderType={nextEntry ? OrderTypes.ENTRY : OrderTypes.STOP}
          />
        );
      },
    },
    {
      title: t("labels.take_profit"),
      dataIndex: "next_take_profit",
      key: "next_take_profit",
      align: "right",
      ...sortable(MyBotsSort.NEXT_TAKE_PROFIT),
      render: (_, botData) => {
        const nextOrder = botData.active_trade?.next_take_profit;
        if (!nextOrder) return empty;
        return (
          <NextOrderCell
            symbol={botData.symbols?.[0]}
            exchange={botData.exchange}
            nextOrder={nextOrder}
            orderType={OrderTypes.TAKE_PROFIT}
          />
        );
      },
    }
  );
  return columns;
};

export default useNewLookBotsTableColumns;

const ICON_STYLE = { marginRight: 0, borderRadius: 8 };
const GROUP_STYLE = { minWidth: 0, maxWidth: "100%", overflow: "hidden" };

const Bot = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 180px;
`;
const Names = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const Name = styled.span`
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 20px;
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Sub = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 260px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
  > * {
    min-width: 0;
  }
`;
const Ellipsis = styled.span`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Dot = styled.span`
  flex: none;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${(p) => getNewLookTokens(p.theme).muted};
  opacity: 0.6;
`;
const End = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  white-space: nowrap;
`;
const Figure = styled.span`
  display: inline-flex;
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Meta = styled.span`
  display: inline-flex;
  align-items: center;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Text = styled.span`
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).text};
  white-space: nowrap;
`;
const Muted = styled.span`
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
